import { useState, useEffect, useRef } from 'react';
import { Search as SearchIcon, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const searchableRoutes = [
  { path: '/services/traumatic-brain-injuries', title: 'Traumatic Brain Injuries', keywords: ['tbi', 'brain injury', 'trauma'] },
  { path: '/services/dysautonomia', title: 'Dysautonomia', keywords: ['autonomic', 'pots', 'orthostatic'] },
  { path: '/services/headaches', title: 'Headaches', keywords: ['migraine', 'tension headache', 'cluster headache'] },
  { path: '/services/fnd', title: 'Functional Neurologic Disorder', keywords: ['functional', 'conversion disorder'] },
  { path: '/services/neurodevelopmental', title: 'Neurodevelopmental Disorders', keywords: ['adhd', 'autism', 'learning'] },
  { path: '/services/movement-disorders', title: 'Movement Disorders', keywords: ['parkinsons', 'tremor', 'dystonia'] },
  { path: '/about', title: 'About Us', keywords: ['team', 'mission', 'history'] },
  { path: '/contact', title: 'Contact', keywords: ['location', 'phone', 'email'] },
  { path: '/patient-info/new-patients', title: 'New Patients', keywords: ['registration', 'forms', 'first visit'] },
  { path: '/patient-info/insurance', title: 'Insurance Information', keywords: ['coverage', 'plans', 'payment'] },
  { path: '/resources/research', title: 'Research', keywords: ['publications', 'studies', 'clinical trials'] },
  { path: '/resources/faqs', title: 'FAQs', keywords: ['questions', 'help', 'information'] },
];

export function Search() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<typeof searchableRoutes>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (searchQuery: string) => {
    setQuery(searchQuery);
    setSelectedIndex(-1);

    if (searchQuery.length > 1) {
      const filtered = searchableRoutes.filter(route => {
        const searchLower = searchQuery.toLowerCase();
        return (
          route.title.toLowerCase().includes(searchLower) ||
          route.keywords.some(keyword => keyword.toLowerCase().includes(searchLower))
        );
      });
      setResults(filtered);
    } else {
      setResults([]);
    }
  };

  const handleSelect = (path: string) => {
    setIsOpen(false);
    setQuery('');
    setResults([]);
    navigate(path);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev < results.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : results.length - 1));
        break;
      case 'Enter':
        if (selectedIndex >= 0) {
          handleSelect(results[selectedIndex].path);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  };

  return (
    <div className="relative" ref={searchRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 text-white hover:text-emory-gold transition-colors"
        aria-label="Search"
      >
        <SearchIcon className="h-5 w-5" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg p-4 z-50">
          <div className="relative">
            <div className="relative">
              <input
                type="text"
                value={query}
                onChange={(e) => handleSearch(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search..."
                className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
              {query && (
                <button
                  onClick={() => {
                    setQuery('');
                    setResults([]);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
            {results.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg max-h-96 overflow-y-auto">
                {results.map((result, index) => (
                  <button
                    key={result.path}
                    onClick={() => handleSelect(result.path)}
                    className={`block w-full text-left px-4 py-2 hover:bg-gray-100 ${
                      index === selectedIndex ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="font-medium text-gray-900">{result.title}</div>
                    {result.keywords.length > 0 && (
                      <div className="text-sm text-gray-500">
                        {result.keywords.join(' • ')}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
            {query.length > 1 && results.length === 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 p-4 text-center text-gray-500 bg-white rounded-lg shadow-lg">
                No results found for "{query}"
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}